'use client';

import React             from 'react';
import {Icon, Separator} from '@glimpse/glass';
import {Button}          from 'foundations/button/button';
import {Accordion}       from '@glimpse/glass';
import {useTranslation}  from 'foundations/i18n/i18n';

export default function PricingPage() {
    const {t} = useTranslation();
    return (
        <div>
            <div className={'pt-8 lg:pt-14 pb-14'}>
                <div className={'site-width'}>
                    <div className={'mb-10'}>
                        <h1 className={'lg:text-center text-3xl lg:text-5xl font-black mb-2'}>{t('Get started for free, upgrade to')} <span className={'text-brand-purple italic'}>scale</span></h1>
                        <span className={'lg:text-center block text-xl'}>{t('Choose the right plan for your team with no minimum seats required.')}</span>
                    </div>
                </div>
                <div className={'max-w-5xl mx-auto'}>
                    <div className={'grid grid-cols-1 lg:grid-cols-2 gap-2 mb-2'}>
                        <Plan
                            name={'Starter'}
                            desc={t('For those who take development seriously')}
                            price={'$24'}
                            highlights={[
                                t('Unlimited Users'),
                                t('Unlimited Components'),
                                t('Unlimited Actions'), t
                                ('Unlimited Scorecards'),
                                t('Unlimited Integrations'),
                            ]}/>
                        <Plan
                            name={'Professional'}
                            desc={t('For those who need something extra')}
                            price={'$46'}
                            highlights={[t('API Access'), t('Advanced Analytics'), t('Custom RBAC'), t('Multi-install integrations')]}/>
                    </div>
                    <div className={'bg-white rounded-lg overflow-hidden flex justify-between items-center p-4'}>
                        <div>
                            <span className={'text-xl font-bold block mb-1'}>{t('Get Started for free')}</span>
                            <span className={'text-slate-500 block'}>{t('Try Glimpse free for 30 days, no credit card required.')}</span>
                        </div>
                        <div>
                            <Button href={'https://app.withglimpse.com'} variant={'primary'}>{t('Start for Free')}</Button>
                        </div>
                    </div>
                </div>
                <div className={'text-center pt-5'}>
                    <span className={'text-lg text-slate-700'}>{t('* Prices are per seat, in USD')}</span>
                </div>
            </div>
            <div className={'pt-14 pb-14 bg-brand-purple mx-2 rounded-2xl'}>
                <div className={'site-width'}>
                    <div className={'mb-12'}>
                        <span className={'text-3xl font-bold mb-1 block text-white'}>{t('Our Commitment to Ethical Pricing')}</span>
                        <div className={'max-w-xl'}>
                            <span className={'text-white opacity-60'}>{t('No contracts, no lock-ins and only ever pay for what you use. Need a contract or alternative pricing?')} </span>
                            <a href={'/contact'} className={'text-white'}>{t('Contact Us.')}</a>
                        </div>
                    </div>
                    <div className={'grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-4'}>
                        <div className={'p-4 bg-g-glass border-slate-50 shadow'}>
                            <Icon fa={['fas', 'users']} className={'text-white text-xl'}/>
                            <span className={'font-bold block mt-2 mb-1 text-white'}>{t('Only pay for what you use')}</span>
                            <span className={'block text-white opacity-60'}>{t('There are no minimum seats. You only pay for exactly the number of people using Glimpse.')}</span>
                        </div>
                        <div className={'p-4 bg-g-glass border-slate-50 shadow'}>
                            <Icon fa={['fas', 'hand-pointer']} className={'text-white text-xl'}/>
                            <span className={'font-bold block mt-2 mb-1 text-white'}>{t('Full self-service available')}</span>
                            <span className={'block text-white opacity-60'}>{t('All billing changes can be done via self-service instantly from your workspace settings.')}</span>
                        </div>
                        <div className={'p-4 bg-g-glass border-slate-50 shadow'}>
                            <Icon fa={['fas', 'calendars']} className={'text-white text-xl'}/>
                            <span className={'font-bold block mt-2 mb-1 text-white'}>{t('No long-term commitments')}</span>
                            <span className={'block text-white opacity-60'}>{t('All pricing plans are monthly, and can be cancelled without penalty.')}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className={'p-14 site-width'}>
                <h2 className={'lg:text-center text-4xl font-bold mb-10'}>{'Frequently Asked Questions'}</h2>
                <div className={'max-w-4xl mx-auto'}>
                    <Accordion title={t('How can I pay for Glimpse?')}>
                        <span>{t('You can use a credit card to pay for any plan.')}</span>
                    </Accordion>
                    <Separator/>
                    <Accordion title={t('Can I upgrade Glimpse for one person, instead of upgrading everyone in our workspace?')}>
                        <span>{t('Unfortunately, no. Glimpse is built to connect the entire team, so you’ll get the most out of it when you all use it together. To upgrade Glimpse, you’ll need to upgrade your entire workspace at once.')}</span>
                    </Accordion>
                    <Separator/>
                    <Accordion title={t('We want to add new users to the team, how will that be billed?')}>
                        <span>{t('We\'ll make a prorated charge to your credit card to cover your new team member for the remainder of your billing period.')}</span>
                    </Accordion>
                    <Separator/>
                    <Accordion title={t('Which currencies do you support?')}>
                        <span>{t('Glimpse is currently available in USD. We are exploring options for additional currencies, so if this is a feature you\'d love, please reach out.')}</span>
                    </Accordion>
                    <Separator/>
                </div>
            </div>
        </div>
    );
}

const Plan = ({
    name,
    desc,
    price,
    highlights
}: {
    name: string,
    desc: string,
    price: string,
    highlights: string[]
}) => {
    const {t} = useTranslation();
    return (
        <div className={`bg-white rounded-lg overflow-hidden p-4`}>
            <div className={'mb-4'}>
                <span className={'text-2xl font-bold block mb-1 text-brand-purple'}>{name}</span>
                <span className={''}>{desc}</span>
            </div>
            <div>
                <div className={'mb-4 border-b-1 border-slate-300'}>
                    <span className={'text-5xl font-black mr-0.5'}>{price}</span>
                    {
                        price !== 'Free' &&
                        <span className={'text-xl font-normal'}>/mo</span>
                    }
                </div>
                <div className={'mb-4'}>
                    <Button href={'https://app.withglimpse.com'}>{t('Get Started')}</Button>
                </div>
                <div className={'border-b-slate-500 min-w-full mb-4'}/>
                <div>
                    <span className={'mb-2 font-bold block'}>{'Highlights:'}</span>
                    {
                        highlights.map(item =>
                            <div key={item}>
                                <Icon fa={['fas', 'fa-check']} className={'mr-1 align-baseline text-brand-success'}/>
                                <span key={item} className={'mb-1 align-baseline'}>{item}</span>
                            </div>
                        )
                    }
                </div>
            </div>
        </div>
    );
};